import { cloneDeep } from "lodash";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { treeActions } from "../../../redux/slices/treeSlice";
import { getUpdatedData } from "./NewRecordOutbound";
import { RECORD_TYPE, DATA_TYPE } from "../../../constants/datatypes";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { genKey, getUniqueWorkflowItemIdFromArray } from "../../../utils/utilFunctions";
import { useLazyGetTrackingReportsQuery } from "../../../redux/api";
import { useLazyTrackingReportAutomappingQuery } from "../../../redux/api";

const NewTrackingReport = ({ toggle, openModal }) => {
    const { t } = useTranslation();
    const parentScreen = useContext(ParentScreenCtx);
    const { leftTree, rightTree } = useSelector((state) => state.treeData[parentScreen]);
    const currentTab = rightTree.currentTab;
    const selectedNode = rightTree.tree[currentTab].selectedNode;
    const templateHeader = rightTree.tree.templateHeader;
    const [showModal, setShowModal] = useState(false);
    const content = t('BOX_LIKE_TO_PLACE_ITEM_INSIDE_CURR_REC_OR_DIRECTLY_AFTER').split(`\\n`).map((str, idx) => <div key={idx}>{str ? str : <br />}</div>);
    const dispatch = useDispatch();
    const { account, user } = useSelector((state) => state.queryParams);
    const [triggerQueryTrackingReportAutomapping] = useLazyTrackingReportAutomappingQuery();
    const [triggerQueryGetTrackingReport] = useLazyGetTrackingReportsQuery();

    // Determine context: outbound or workflow
    const isWorkflow = parentScreen === 'workflow';
    const keyPrefix = isWorkflow ? 'wf' : {header: "obh-0", body: "ob-0", trailer: "obt-0"}[currentTab];

    const handleNewField = async (isInside) => {
        let nestedLevels = selectedNode?.key?.split("-");
        nestedLevels = nestedLevels?.splice(1);
        const translatedData = cloneDeep(rightTree.tree[currentTab].translatedData);
        const newId = getUniqueWorkflowItemIdFromArray(translatedData);

        // Function For Getting New Record Title and Key
        const getNewFieldData = (data, key) => {
            //If it is empty so it will make first node with 'ib-0' key
            if (!data.length) return { key: `${key}-0`, title: `${t('UNNAMED')}1`, insertingPosition: 1 };

            let latestNewNodeNum = 0;
            let insertingPosition = 1;
            data.forEach((ele, idx) => {
                // Checking if Unnamed is present already for creating next unnamed record
                const fieldTitle = ele?.MAPNAME;
                if (fieldTitle && fieldTitle.startsWith(`${t("UNNAMED")}`)) {
                    const latestUnnamedRecord = Number(fieldTitle.slice(t("UNNAMED").length));
                    if (!isNaN(latestUnnamedRecord) && DATA_TYPE[templateHeader?.FILETYPE]?.field?.icon === ele?.ICON_DISPLAY) {
                        latestNewNodeNum =
                            latestUnnamedRecord > latestNewNodeNum
                                ? latestUnnamedRecord
                                : latestNewNodeNum;
                    }
                }
                if (ele.key === selectedNode.key) {
                    insertingPosition = idx + 2;
                }
            });

            const lastRecordKey = data?.[data.length - 1]?.key?.split("-");
            lastRecordKey[lastRecordKey.length - 1] = +lastRecordKey[lastRecordKey.length - 1] + 1;
            const newRecordKey = lastRecordKey.join("-");
            return {
                key: newRecordKey,
                title: `${t('UNNAMED')}${latestNewNodeNum ? +latestNewNodeNum + 1 : 1}`,
                insertingPosition,
            };
        };

        const {data: {tracking_reports: trackingReports}} = await triggerQueryGetTrackingReport({
            account, user
        })

        const {data: trackingReportDetails} = await triggerQueryTrackingReportAutomapping({
            data: {
                account_name: account,
                user_name: user,
                statement_type: 'INSERT',
                selected_tr_name: trackingReports?.[0]?.name || "",
                in_template: leftTree?.inboundDependencyTree?.templateHeader?.FILETYPE || "",
                out_template: rightTree?.tree?.templateHeader?.FILETYPE || "",
                template_name: leftTree?.tree?.body?.selectedNode?.title || "",
                wfg: leftTree?.tree?.body?.selectedNode?.workflowGroup || "",
                is_workflow: isWorkflow
            },
        }).unwrap();

        // Inserting New Node Object in Tree Data (Recursive Function);
        function insertNodeInNestedTree(translatedData, nestedLevels, isInside) {
            const level = isInside ? 0 : 1;
            if (nestedLevels?.length === level) {
                const { children, key } = translatedData;
                const newFieldDetails = children
                    ? getNewFieldData(children, key)
                    : getNewFieldData(translatedData, keyPrefix);
                const newNode = isWorkflow ? {
                    nodeId: genKey(newFieldDetails.title),
                    alternateNodeId: genKey(newFieldDetails.title),
                    key: newFieldDetails.key,
                    title: `${t("LBL_TRACKING_REPORT")} ${t("UNNAMED")}`,
                    ACTION: "TRACKING_REPORT_ITEM",
                    CONCURRENT: "FALSE",
                    ENABLED: "TRUE",
                    ID: newId,
                    LOG_WFITEM_DETAILS: "TRUE",
                    NAME: "Tracking Report",
                    ICON_DISPLAY: "TRACKING_REPORT_ITEM",
                    TRACKING_REPORT_ITEM: {
                        NAME: "Tracking Report",
                        ON_STATEMENT_ERROR_FLIP: "true",
                        TRI_RPT_NAME: trackingReports?.[0]?.name || "UNNAMED",
                        TRI_STMT_TYPE: "INSERT",
                        UNIQUE: "false",
                        TRACKING_REPORT_KEY: Object.keys(trackingReportDetails).length ?
                            Object.values(trackingReportDetails).map((field) => {
                                let returnObj = { ...field };
                                if(returnObj.hasOwnProperty("IS_KEY"))
                                    returnObj.IS_KEY = (returnObj.IS_KEY === "TRUE") ? "Y" : "N";
                                return returnObj;
                            }) : [],
                    },
                    children: [],
                    fieldNumber: newFieldDetails.insertingPosition,
                } : {
                    nodeId: genKey(newFieldDetails.title),
                    alternateNodeId: genKey(newFieldDetails.title),
                    key: newFieldDetails.key,
                    title: `${t("LBL_TRACKING_REPORT")} ${t("UNNAMED")}`,
                    DESCRIPTION: "",
                    NAME: t('UNNAMED'),
                    ON_STATEMENT_ERROR_FLIP: true,
                    TRI_RPT_NAME: trackingReports?.[0]?.name || "UNNAMED",
                    TRI_STMT_TYPE: "INSERT",
                    TYPE: "TRACKING_REPORT_ITEM",
                    UNIQUE: false,
                    ICON_DISPLAY: "TRACKING_REPORT_ITEM",
                    TRACKING_REPORT_DETAILS: Object.keys(trackingReportDetails).length ?
                        Object.values(trackingReportDetails).map((field) => {
                            let returnObj = { ...field };
                            if(returnObj.hasOwnProperty("IS_KEY"))
                                returnObj.IS_KEY = (returnObj.IS_KEY === "TRUE") ? "Y" : "N";
                            return { TRACKING_REPORT_FIELD: returnObj }
                        }) : [],
                    OUTBOUND_ACTION_NAME: "",
                    children: [],
                    // MAPNAME: newFieldDetails.title,
                    fieldNumber: newFieldDetails.insertingPosition,
                };

                if (children) {
                    if (isInside) {
                        translatedData.children.splice(0, 0, newNode);
                        let newKey = selectedNode?.key?.split('-');
                        newKey.push('0')
                        newKey = newKey.join('-');
                        dispatch(treeActions.setRightTreeSelectedNode({
                            screen: parentScreen,
                            type: currentTab,
                            data: { ...newNode, key: newKey }
                        }));
                    } else {
                        const tempKey = selectedNode?.key?.split('-');
                        const lastIndex = + tempKey[tempKey.length-1] + 1

                        if(selectedNode?.ICON_DISPLAY === 'CONDITION_IF' && translatedData?.children[lastIndex]?.ICON_DISPLAY === "CONDITION_ELSE"){
                            translatedData.children.splice(lastIndex+1, 0, newNode);
                            let newKey = selectedNode?.key?.split('-');
                            newKey[newKey.length - 1] = Number(newKey[newKey.length - 1]) + 2;
                            newKey = newKey.join('-');
                            //  newNode.RECORD_TYPE = selectedNode?.RECORD_TYPE;
                            dispatch(treeActions.setRightTreeSelectedNode({
                                screen: parentScreen,
                                type: currentTab,
                                data: { ...newNode, key: newKey }
                            }));
                        }
                        else{
                        translatedData.children.splice(newFieldDetails.insertingPosition - 1, 0, newNode);
                        let newKey = selectedNode?.key?.split('-');
                        newKey[newKey.length - 1] = Number(newKey[newKey.length - 1]) + 1;
                        newKey = newKey.join('-');
                        //  newNode.RECORD_TYPE = selectedNode?.RECORD_TYPE;
                        dispatch(treeActions.setRightTreeSelectedNode({
                            screen: parentScreen,
                            type: currentTab,
                            data: { ...newNode, key: newKey }
                        }));
                    }
                }
                }
                return;
            }

            const isArray = Array.isArray(translatedData);
            if (isArray) {
                const nestedData = translatedData[[nestedLevels[0]]];
                const newNestedLevels = nestedLevels.splice(1);
                insertNodeInNestedTree(nestedData, newNestedLevels, isInside);
            } else {
                // const newNestedLevels = nestedLevels.splice(1);
                insertNodeInNestedTree(translatedData.children, nestedLevels, isInside);
            }
        }
        insertNodeInNestedTree(translatedData, nestedLevels, isInside);
        const updatedData = getUpdatedData(translatedData, templateHeader?.FILETYPE, null, isWorkflow, currentTab);
        dispatch(
            treeActions.setNewRightTreeTranslatedData({
                screen: parentScreen,
                type: currentTab,
                data: updatedData,
            })
        );
        toggle(null);
        setShowModal(false);
    };

    const newConditionalHandler = () => {
        if (selectedNode?.key?.split("-").length === 2) {
            handleNewField(true)
        }
        else if (selectedNode.ICON_DISPLAY === "QUERY") {
            handleNewField(false)
        }
        else if (["CONDITION_IF", "CONDITION_ELSE", "LOOP_ITEM"].includes(selectedNode.ICON_DISPLAY) && openModal) {
            setShowModal(true);
        }
        else if (!RECORD_TYPE[selectedNode.ICON_DISPLAY] && openModal) {
            handleNewField(false);
        }
        else {
            setShowModal(true);
        }
    }
    useEffect(() => {
        if (openModal) {
            newConditionalHandler();
        }
    }, [openModal])

    return (
        <Dialog open={showModal} maxWidth="xs" fullWidth>
            <DialogTitle className="dialog-header">
                <div className="title">
                    {t("BOX_CONFIRM")}
                </div>
            </DialogTitle>
            <DialogContent className="dialog-content" dividers>
                <div>{content}</div>
            </DialogContent>
            <DialogActions>
                <Button size='small' variant="outlined" color="primary" onClick={() => handleNewField(true)} >
                    {t("BTN_YES")}
                </Button>
                <Button size='small' variant="outlined" color="primary" onClick={() => handleNewField(false)} >
                    {t("BTN_NO")}
                </Button>
                <Button size='small' variant="outlined" color="primary" onClick={() => {
                    toggle(null);
                    setShowModal(false);
                }} >
                    {t("BTN_CANCEL")}
                </Button>
            </DialogActions>
        </Dialog>
    )
};


export default NewTrackingReport;